import { fetchAuthSession } from "aws-amplify/auth";
import { COMPETITION_API_BASE } from "./competitionApiEnvironment";

const API_BASE = COMPETITION_API_BASE;

async function getAuthHeaders(required = false) {
  try {
    const session = await fetchAuthSession();
    const token =
      session?.tokens?.idToken?.toString() ||
      session?.tokens?.accessToken?.toString() ||
      "";

    if (token) {
      return { authorization: `Bearer ${token}` };
    }
  } catch {
    // Fall through to required check below.
  }

  if (required) {
    throw new Error("Sign in required");
  }

  return {};
}

async function request(path, options = {}) {
  const { auth = false, headers: providedHeaders = {}, ...fetchOptions } = options;
  const authHeaders = await getAuthHeaders(auth);
  const response = await fetch(`${API_BASE}${path}`, {
    headers: {
      "content-type": "application/json",
      ...authHeaders,
      ...providedHeaders,
    },
    ...fetchOptions,
  });
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message = payload?.error?.message || `Request failed (${response.status})`;
    throw new Error(message);
  }

  return payload;
}

export async function getPublicAffiliatePartner(partnerCode) {
  return request(`/affiliates/public/${encodeURIComponent(partnerCode)}`);
}

export async function recordAffiliateEvent(eventPayload) {
  return request("/affiliates/events", {
    method: "POST",
    body: JSON.stringify(eventPayload),
  });
}

export function recordAffiliateEventBeforeNavigation(eventPayload) {
  const url = `${API_BASE}/affiliates/events`;
  const body = JSON.stringify(eventPayload);

  if (navigator.sendBeacon) {
    const sent = navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    if (sent) return;
  }

  fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}

export async function getMyAffiliatePartner() {
  return request("/affiliates/me", { auth: true });
}

export async function createAffiliateApplication(payload) {
  return request("/affiliates/applications", {
    method: "POST",
    auth: true,
    body: JSON.stringify(payload),
  });
}

export async function patchMyAffiliatePartner(payload) {
  return request("/affiliates/me", {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(payload),
  });
}

export async function listAdminAffiliatePartners({ status = "", limit = 100, nextToken = "" } = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (status) {
    params.set("status", status);
  }
  if (nextToken) {
    params.set("nextToken", nextToken);
  }
  return request(`/admin/affiliates?${params.toString()}`, { auth: true });
}

export async function patchAdminAffiliatePartner(partnerCode, payload) {
  return request(`/admin/affiliates/${encodeURIComponent(partnerCode)}`, {
    method: "PATCH",
    auth: true,
    body: JSON.stringify(payload),
  });
}
